
import React from 'react';
import { Badge } from '@/components/ui/badge';
import { 
  HoverCard,
  HoverCardContent,
  HoverCardTrigger 
} from '@/components/ui/hover-card';
import { Share2, HelpCircle, TrendingUp } from 'lucide-react';

interface InsightsPanelProps {
  symptomData?: any[];
  selectedSymptoms?: string[];
}

export const InsightsPanel: React.FC<InsightsPanelProps> = ({ 
  symptomData = [], 
  selectedSymptoms = [] 
}) => {
  // Get co-occurring symptoms pairs
  const getCorrelatedSymptoms = React.useMemo(() => {
    if (symptomData.length === 0 || selectedSymptoms.length < 2) return [];
    
    const symptomPairs: Record<string, number> = {};
    
    // Count co-occurrences of symptoms
    symptomData.forEach(entry => {
      const entrySymptoms = entry.symptoms.filter((s: string) => 
        selectedSymptoms.includes(s)
      );
      
      // If two or more selected symptoms occur together
      if (entrySymptoms.length >= 2) {
        // Create pairs and count occurrences
        for (let i = 0; i < entrySymptoms.length; i++) {
          for (let j = i + 1; j < entrySymptoms.length; j++) {
            const pair = [entrySymptoms[i], entrySymptoms[j]].sort().join('_with_');
            symptomPairs[pair] = (symptomPairs[pair] || 0) + 1;
          }
        }
      }
    });
    
    // Convert to array and sort by frequency
    return Object.entries(symptomPairs)
      .map(([pair, count]) => {
        const [symptom1, symptom2] = pair.split('_with_');
        return { symptom1, symptom2, count };
      })
      .sort((a, b) => b.count - a.count)
      .slice(0, 3); // Top 3 correlations
  }, [symptomData, selectedSymptoms]);
  
  return (
    <div className="mt-6 bg-white rounded-xl shadow-sm border border-hormony-beige/30 p-4 md:p-6">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-medium">Insights & Recommendations</h2>
        <HoverCard>
          <HoverCardTrigger asChild>
            <div className="cursor-help">
              <HelpCircle size={16} className="text-hormony-charcoal/60" />
            </div>
          </HoverCardTrigger>
          <HoverCardContent className="w-80">
            <div className="space-y-2">
              <h4 className="font-medium">About Insights</h4>
              <p className="text-sm text-hormony-charcoal/70">
                These insights are generated based on your symptom tracking data. The more 
                consistently you track your symptoms, the more accurate these insights will be.
              </p>
            </div>
          </HoverCardContent>
        </HoverCard>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-2">
          <Badge className="bg-hormony-coral/10 text-hormony-coral hover:bg-hormony-coral/20">
            Pattern Detected
          </Badge>
          <h3 className="font-medium">Most Frequent Symptoms</h3>
          <p className="text-hormony-charcoal/70 text-sm">
            Based on your tracked data, your most frequently reported symptoms are mood swings, 
            fatigue, and bloating. These symptoms often appear in patterns that may correlate 
            with your hormonal cycle.
          </p>
        </div>
        
        <div className="space-y-2">
          <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-200">
            Recommendation
          </Badge>
          <h3 className="font-medium">Share With Your Provider</h3>
          <p className="text-hormony-charcoal/70 text-sm">
            Consider exporting your symptom data and sharing it with your healthcare provider. 
            This data can help them better understand your symptoms over time and may lead to 
            more informed treatment decisions. Use the Export button to download your data.
          </p>
        </div>
        
        {getCorrelatedSymptoms.length > 0 && (
          <div className="space-y-2 md:col-span-2">
            <Badge className="bg-purple-100 text-purple-700 hover:bg-purple-200 flex items-center gap-1">
              <TrendingUp size={14} />
              Correlation Analysis
            </Badge>
            <h3 className="font-medium">Symptom Relationships</h3>
            <p className="text-hormony-charcoal/70 text-sm mb-3">
              We've identified patterns where certain symptoms tend to occur together. This might help you
              and your healthcare provider better understand your symptom triggers.
            </p>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
              {getCorrelatedSymptoms.map(({ symptom1, symptom2, count }, index) => (
                <div key={index} className="p-3 bg-hormony-beige/10 rounded-lg border border-hormony-beige/20">
                  <p className="text-sm font-medium">{symptom1} + {symptom2}</p>
                  <p className="text-xs text-hormony-charcoal/70">
                    Occurred together {count} {count === 1 ? 'time' : 'times'}
                  </p>
                </div>
              ))}
              
              {getCorrelatedSymptoms.length === 0 && (
                <div className="p-3 bg-gray-50 rounded-lg border border-gray-100">
                  <p className="text-sm text-gray-500 italic">
                    Select more symptoms or log more data to see correlations
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
